/**
 * A self-contained search bar component for embedding on external websites
 * (e.g., Turquoise marketing site). Fetches its UI from the Consumer app
 * and renders inside a Shadow DOM for style encapsulation.
 *
 * Usage on external site:
 *
 *   <script type="module" src="https://tq-consumer-static-prd.turquoise.health/static/js/tqc-search-bar.js"></script>
 *   <tqc-search-bar></tqc-search-bar>
 *
 * Attributes:
 *
 *   - base-url (optional): for testing embedding on another locally-running application
 *       - Defaults to "" (same origin as the JS file)
 *       - Use for local dev: base-url="http://localhost:8000"
 *
 * How it works:
 *
 *   1. Component fetches GET /care/search_bar/ from Consumer
 *   2. Response is full HTML with embedded stylesheets, SVG sprites, and JS
 *   3. Full HTML doc (including scripts and styles) is injected into Shadow DOM
 *   4. Form submits to /care/search/ with target="_top" (the document outside the Shadow DOM)
 *
 */
class TQCSearchBar extends HTMLElement {
  constructor() {
    super();

    this.baseURL = this.getAttribute("base-url") || "";
    this.style.background = "transparent";
    this.style.display = "none";
  }

  connectedCallback() {
    // create the shadow dom and fill it with html
    this.shadow = this.attachShadow({ mode: "open" });
    this._fetchSearchBar();

    // build and add a meta tag to the document head so map box queries inside the shadowdom work
    const metaEl = document.createElement("meta");
    metaEl.name = "referrer";
    metaEl.content = "origin";
    this.metaEl = metaEl;
    this.parentElement.appendChild(metaEl);
  }

  async _fetchSearchBar() {
    // fetch the search bar html
    const response = await fetch(`${this.baseURL}/care/search_bar/`, {
      credentials: "include",
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const text = await response.text();

    // inject all the html
    this.shadow.innerHTML = text;

    // rebuild the script tags inside the shadow dom so they actually execute
    for (const script of this.shadow.querySelectorAll("script")) {
      if (script.id) {
        // scripts with ids are json config blobs, not code to execute
        if (script.id === "page-config" && this.baseURL) {
          // gross hack to include the base URL in some url fields in the JSON config
          // when deployed this wont really matter as everything will be on a
          // turquoise.health domain, but locally when crossing between :8000
          // and :8001 this is very helpful.
          let config = JSON.parse(script.textContent);
          config["servicesUrl"] = `${this.baseURL}${config["servicesUrl"]}`;
          config["analyticsUrl"] = `${this.baseURL}${config["analyticsUrl"]}`;
          script.text = JSON.stringify(config);
        }
      } else {
        const scriptCopy = document.createElement("script");
        scriptCopy.text = script.text;
        scriptCopy.src = script.src;
        scriptCopy.type = script.type;
        if (scriptCopy.src.includes("htmx")) {
          const shadow = this.shadow;
          scriptCopy.addEventListener("load", () => {
            window.htmx.process(shadow);
          });
        }
        this.shadow.appendChild(scriptCopy);
      }
    }

    const links = this.shadow.querySelectorAll("link[rel='stylesheet']"),
      linkCount = links.length;

    let stylesheetsLoaded = 0;

    for (const link of links) {
      link.onload = () => {
        stylesheetsLoaded += 1;

        if (linkCount <= stylesheetsLoaded) {
          this.style.display = "unset";
        }
      };
    }

    this._deleteContactUsSuccessHandler();
    this._ready = true;
  }

  async launchContactUsModal() {
    if (!this._ready) return;
    await customElements.whenDefined("contact-us");
    this.shadowRoot.querySelector("contact-us").openContactModal("Service");
  }

  async launchInsuranceSelector() {
    if (!this._ready) return;
    await customElements.whenDefined("insurance-selector-dialog");
    this.scrollIntoView({ behavior: "smooth" });
    this.shadowRoot.dispatchEvent(
      new Event("tqc:insurance-selector:open", { bubbles: true }),
    );
  }

  /**
   * When the request a plan / service form submit succeeds, it inserts a toast message.
   *
   * In the embedded context we want to prevent this from happening,
   * because the toast depends on pit viper styles
   * that are not loaded in the document hosting the shadow root.
   */
  _deleteContactUsSuccessHandler() {
    const contactForm = this.shadow.getElementById("contact-us-form");
    if (contactForm) {
      contactForm.setAttribute("hx-swap", "none");
      contactForm.removeAttribute("hx-target");
    }
  }

  disconnectedCallback() {
    this.parentElement.removeChild(this.metaEl);
  }
}

customElements.define("tqc-search-bar", TQCSearchBar);
